import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { validatePhone } from "@/utils/functions";
import { setUser } from "@/redux/ducks/user";
import { NavigateFunction } from "react-router-dom";

const personalDataAvulsoSchema = z.object({
  name: z.string().min(2, "Nome obrigatório"),
  email: z.string().email("E-mail inválido"),
  phone: z
    .string()
    .regex(/^\(\d{2}\) \d{5}-\d{4}$/, "Celular inválido")
    .refine((val) => validatePhone(val), "Celular inválido"),
});

export default function usePersonalDataFormAvulso(navigate: NavigateFunction) {
  const dispatch = useDispatch();

  type FormData = z.infer<typeof personalDataAvulsoSchema>;

  const defaultValues: FormData = {
    name: "",
    email: "",
    phone: "",
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<FormData>({
    resolver: zodResolver(personalDataAvulsoSchema),
    mode: "onChange",
    defaultValues,
  });

  const watchedValues = watch();
  const { name, email, phone } = watchedValues;

  const onSubmit = useCallback(
    (data: FormData) => {
      // Salva os dados no userReducer para uso no fluxo avulso
      dispatch(
        setUser({
          name: data.name,
          email: data.email,
          phone: data.phone,
        }),
      );
      navigate("/register/choosepin");
      reset();
    },
    [dispatch, reset, navigate],
  );

  const isNameValid = name && name.trim().length >= 2;
  const isEmailValid = email && email.includes("@") && email.includes(".");
  const isPhoneValid = phone && phone.length === 15 && validatePhone(phone);

  const isFormValid = isNameValid && isEmailValid && isPhoneValid;

  return {
    isDisabled: !isFormValid,
    register,
    handleSubmit: handleSubmit(onSubmit),
    errors,
    watch,
  };
}
